import { generateHTMLFromRTE } from '@/components/blog-templates';
import type { ContentDetail } from '@/components/BlogCreationCommandCenter/types';
import { useTenantStore } from '@/stores/useTenantStore';
import type { CalendarItem } from '@/types/contentPlanner';
import { blogCreationService } from './blogCreationService';
import { isPublishCancelledError } from '@/lib/prePublishGate';

export type BulkActionStatus = 'success' | 'failed' | 'skipped';

export interface ContentPlannerBulkActionResult {
  content_id: string;
  tenant_id: string | null;
  status: BulkActionStatus;
  wordpress_post_id?: number;
  post_url?: string;
  error?: string;
}

export interface ContentPlannerBulkActionSummary {
  succeeded: number;
  failed: number;
  skipped: number;
  results: ContentPlannerBulkActionResult[];
}

export interface PublishContentPlannerItemsOptions {
  fallbackTenantId?: string | null;
  confirmLinkWarnings?: (item: CalendarItem, detail: ContentDetail) => Promise<boolean> | boolean;
}

export interface DraftContentPlannerItemsOptions {
  fallbackTenantId?: string | null;
  syncExistingPosts?: boolean;
}

interface PublishPayload {
  content_id: string;
  html_content: string;
  builder_native_override?: boolean;
}

function fallbackTenantId(): string | null {
  return useTenantStore.getState().currentTenant || null;
}

export function tenantForContentPlannerItem(
  item: Pick<CalendarItem, '_wsTenant' | 'tenant_id'>,
  fallback?: string | null,
): string | null {
  return item._wsTenant || item.tenant_id || fallback || fallbackTenantId();
}

function summarize(results: ContentPlannerBulkActionResult[]): ContentPlannerBulkActionSummary {
  return {
    succeeded: results.filter((result) => result.status === 'success').length,
    failed: results.filter((result) => result.status === 'failed').length,
    skipped: results.filter((result) => result.status === 'skipped').length,
    results,
  };
}

function contentHtmlFromDetail(detail: ContentDetail, siteHost?: string | null): string {
  if (detail.content_json) {
    return generateHTMLFromRTE(detail.content_json, siteHost);
  }
  return detail.content_html || '';
}

function shouldSyncDraftToWordPress(item: CalendarItem, options: DraftContentPlannerItemsOptions): boolean {
  if (options.syncExistingPosts === false) {
    return false;
  }
  return Boolean(item.wordpress_post_id || item.wp_url);
}

export async function publishContentPlannerItems(
  items: CalendarItem[],
  options: PublishContentPlannerItemsOptions = {},
): Promise<ContentPlannerBulkActionSummary> {
  const results: ContentPlannerBulkActionResult[] = [];
  const payloadsByTenant = new Map<string, PublishPayload[]>();

  for (const item of items) {
    const tenantId = tenantForContentPlannerItem(item, options.fallbackTenantId);
    if (!tenantId) {
      results.push({
        content_id: item.id,
        tenant_id: null,
        status: 'failed',
        error: 'Missing tenant for content item',
      });
      continue;
    }

    try {
      const detail = await blogCreationService.get(item.id, tenantId);
      if (options.confirmLinkWarnings) {
        const proceed = await options.confirmLinkWarnings(item, detail);
        if (!proceed) {
          results.push({
            content_id: item.id,
            tenant_id: tenantId,
            status: 'skipped',
          });
          continue;
        }
      }

      const htmlContent = contentHtmlFromDetail(detail, tenantId);
      if (!htmlContent) {
        throw new Error('No content available to publish');
      }

      const payload: PublishPayload = {
        content_id: item.id,
        html_content: htmlContent,
      };
      // Bridge is off across the UI for now — publish everything via Legacy, even
      // rows that were created earlier with use_bridge = true.
      payload.builder_native_override = false;

      const tenantPayloads = payloadsByTenant.get(tenantId) || [];
      tenantPayloads.push(payload);
      payloadsByTenant.set(tenantId, tenantPayloads);
    } catch (error) {
      results.push({
        content_id: item.id,
        tenant_id: tenantId,
        status: 'failed',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  for (const [tenantId, payloads] of payloadsByTenant) {
    try {
      const response = await blogCreationService.batchPublish(payloads, { tenantId });
      for (const result of response.results) {
        results.push({
          content_id: result.content_id,
          tenant_id: tenantId,
          status: result.success ? 'success' : 'failed',
          wordpress_post_id: result.wordpress_post_id,
          post_url: result.post_url,
          error: result.error,
        });
      }
    } catch (error) {
      // "Go back" in the pre-publish metadata gate cancels the whole batch —
      // mark those items 'skipped' (a neutral, non-error outcome), not 'failed',
      // so the caller shows an info/no toast rather than a false error.
      const cancelled = isPublishCancelledError(error);
      for (const payload of payloads) {
        results.push({
          content_id: payload.content_id,
          tenant_id: tenantId,
          status: cancelled ? 'skipped' : 'failed',
          error: cancelled ? undefined : error instanceof Error ? error.message : String(error),
        });
      }
    }
  }

  return summarize(results);
}

export async function draftContentPlannerItems(
  items: CalendarItem[],
  options: DraftContentPlannerItemsOptions = {},
): Promise<ContentPlannerBulkActionSummary> {
  const settled = await Promise.allSettled(
    items.map(async (item): Promise<ContentPlannerBulkActionResult> => {
      const tenantId = tenantForContentPlannerItem(item, options.fallbackTenantId);
      if (!tenantId) {
        throw new Error('Missing tenant for content item');
      }

      await blogCreationService.updateWordPressStatus(item.id, 'draft', {
        tenantId,
        syncImmediately: shouldSyncDraftToWordPress(item, options),
      });

      return {
        content_id: item.id,
        tenant_id: tenantId,
        status: 'success',
      };
    }),
  );

  const results = settled.map((result, index): ContentPlannerBulkActionResult => {
    const item = items[index];
    const tenantId = tenantForContentPlannerItem(item, options.fallbackTenantId);
    if (result.status === 'fulfilled') {
      return result.value;
    }
    return {
      content_id: item.id,
      tenant_id: tenantId,
      status: 'failed',
      error:
        result.reason instanceof Error
          ? result.reason.message
          : String(result.reason),
    };
  });

  return summarize(results);
}
